import React, { useState, useEffect } from 'react';
import QuizQuestion from './ArithmeticQuizQuestion';
import './ArithmeticQuiz.css';
import { Icon } from '@iconify/react';
import { getRandomQuestion } from './ArithmeticGrade7'; // Ensure this function imports correctly

const grades = [7, 8, 9]; // Available grades
const topics = ['Algebra', 'Trigonometry', 'Calculus', 'Miscellaneous']; // Updated topics

function ArithmeticQuiz() {
  const [grade, setGrade] = useState(7);
  const [topic, setTopic] = useState('Algebra'); // Default topic set to Algebra
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [score, setScore] = useState(0);
  const [questionsAnswered, setQuestionsAnswered] = useState(0);
  const [scoreboard, setScoreboard] = useState([]);
  const [showScoreboard, setShowScoreboard] = useState(false);
  const [time, setTime] = useState(0);
  const [isRunning, setIsRunning] = useState(false);

  const gradeQuestionFiles = {
    7: () => import('./ArithmeticGrade7'),
    8: () => import('./ArithmeticGrade8'),
    9: () => import('./ArithmeticGrade9'),
  };
  

  // Function to get a random question based on the selected topic
  const fetchRandomQuestion = async () => {
    const module = await gradeQuestionFiles[grade]();
    return module.getRandomQuestion(topic); // Ensure this function exists in each module
  };

  
  useEffect(() => {
    const getQuestion = async () => {
      const question = await fetchRandomQuestion();
      setCurrentQuestion(question);
    };
    getQuestion();
  }, [topic, grade]);
  

  // Initial question setup
  useEffect(() => {
    setCurrentQuestion(fetchRandomQuestion());
  }, [topic]);

  // Stopwatch logic
  useEffect(() => {
    let timer;
    if (isRunning) {
      timer = setInterval(() => {
        setTime((prevTime) => prevTime + 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [isRunning]);

  const handleAnswer = async (selectedAnswer) => {
    console.log('Selected Answer:', selectedAnswer);
    console.log('Correct Answer:', currentQuestion.correct);
  
    const isCorrect = selectedAnswer.toString() === currentQuestion.correct;
  
    if (isCorrect) {
      setScore(score + 1);
    }
  
    setQuestionsAnswered(questionsAnswered + 1);
  
    // Update the scoreboard
    setScoreboard((prevScoreboard) => [
      {
        questionNumber: questionsAnswered + 1,
        question: currentQuestion.question,
        selectedAnswer: selectedAnswer,
        correctAnswer: currentQuestion.correct,
        isCorrect: isCorrect,
      },
      ...prevScoreboard,
    ]);
  
    // Fetch a new question
    const newQuestion = await fetchRandomQuestion();
    if (newQuestion) {
      setCurrentQuestion(newQuestion);
    } else {
      console.error('Failed to fetch a new question');
    }
  };
  

  const startStopwatch = () => setIsRunning(true);
  const pauseStopwatch = () => setIsRunning(false);
  const resetStopwatch = () => {
    setIsRunning(false);
    setTime(0);
  };

  const formatTime = (timeInSeconds) => {
    const hours = Math.floor(timeInSeconds / 3600);
    const minutes = Math.floor((timeInSeconds % 3600) / 60);
    const seconds = timeInSeconds % 60;
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const calculatePercentage = () => {
    return questionsAnswered === 0 ? 0 : ((score / questionsAnswered) * 100).toFixed(2);
  };


  const correctIconStyle = {
    color: '#32cd32', // Green for correct answers
    marginRight: '5px', // Adjust icon spacing
    verticalAlign: 'middle', // Align icon vertically with text
  }; 

  const incorrectIconStyle = {
    color: '#ff0000', // Red for incorrect answers
    marginRight: '5px', // Adjust icon spacing
    verticalAlign: 'middle', // Align icon vertically with text
  };
  
  return (
    <div className="quiz-container">
      <h2>Arithmetic Quiz - Grade {grade}</h2>

      {/* Grade Selection */}
      <label>
        Select Grade:
        <select value={grade} onChange={(e) => setGrade(Number(e.target.value))}>
          {grades.map((g) => (
            <option key={g} value={g}>
              Grade {g}
            </option>
          ))}
        </select>
      </label>

      {/* Topic Selection */}
      <label>
        Select Topic:
        <select value={topic} onChange={(e) => setTopic(e.target.value)}>
          {topics.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </label>

      <h3>Questions answered: {questionsAnswered}</h3>

      {/* Stopwatch */}
      <div className="stopwatch-container">
        <h3>Time: {formatTime(time)}</h3>
        {isRunning ? (
          <button onClick={pauseStopwatch}>Pause</button>
        ) : (
          <button onClick={startStopwatch}>Start</button>
        )}
        <button onClick={resetStopwatch}>Reset</button>
      </div>

      {currentQuestion && (
        <QuizQuestion question={currentQuestion} handleAnswer={handleAnswer} />
      )}

      <button onClick={() => setShowScoreboard(!showScoreboard)}>
        {showScoreboard ? 'Hide Scoreboard' : 'Show Scoreboard'}
      </button>

      {showScoreboard && (
        <div className="scoreboard-container">
          <h3>Score: {score}/{questionsAnswered} ({calculatePercentage()}%)</h3>
          <h4>Scoreboard</h4>
          <ul>
            {scoreboard.map((entry, index) => (
              <li key={index}>
                <span>{entry.questionNumber}. {entry.question}</span>
                <span> - Your Answer: {entry.selectedAnswer}</span>
                <span> - Correct Answer: {entry.correctAnswer}</span>
                <span> - {entry.isCorrect ? (
                  <Icon icon="icon-park-solid:correct" width="24" height="24" style={correctIconStyle} />
                ) : (
                  <Icon icon="icomoon-free:cross" width="24" height="24" style={incorrectIconStyle} />
                )}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default ArithmeticQuiz;
